import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import Calendar from 'components/shared/Calendar'
import {
    ModalHeader,
    ModalFooter,
} from 'components/global/CalendarModal/styles'
import { formatDates } from 'helpers/dates'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage } from 'react-intl'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { useListTicketAvailableDaysQuery } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { closeModalActions } from 'helpers/helpers'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'
import CalendarModalLegend from 'components/global/CalendarModal/components/CalendarModalLegend'

interface CalendarProps {
    isOpened: boolean
    customConfirmDate: (date: string) => void
    close: () => void
    openHoursModal: () => void
    currentDate: string
    testIdPrefix?: string
    isCurrentDayDisabled?: boolean
}

const SelfcareCalendarModal = ({
    isOpened,
    close,
    customConfirmDate,
    openHoursModal,
    currentDate,
    testIdPrefix,
    isCurrentDayDisabled = false,
}: CalendarProps) => {
    const { language } = useApp()
    const [isDisabledCalendar, setIsDisabledCalendar] = useState(false)
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [unconfirmedDates, setUnconfirmedDates] = useState<string[]>(
        currentDate === '' ? [] : [currentDate]
    )
    const { isMobile } = useResolutions()
    const { data, isFetching } = useListTicketAvailableDaysQuery(
        {
            _locale: language,
        },
        { skip: !isOpened }
    )

    const isEventsLegendVisible = !!data?.events?.length
    const isHolidaysLegendVisible = !isEventsLegendVisible

    const handleSetDates = (dates: Moment[]) => {
        if (dates.length > 1) {
            const fullDatesRange = []

            for (
                let date = moment(dates[0]);
                date.isSameOrBefore(dates[1]);
                date.add(1, 'days')
            ) {
                fullDatesRange.push(date.format('YYYY-MM-DD'))
            }
            setUnconfirmedDates(fullDatesRange)
        } else {
            setUnconfirmedDates([dates[0].format('YYYY-MM-DD')])
        }
        if (!isMobile) {
            setIsConfirmed(true)
        }
    }

    const confirmDates = () => {
        customConfirmDate(unconfirmedDates[0])

        const closeModal = () => {
            setIsConfirmed(false)
            setIsDisabledCalendar(false)
            close()
            clearTimeout(timeout)
            closeModalActions(false)
        }

        const timeout = setTimeout(closeModal, 500)
    }

    useEffect(() => {
        if (isConfirmed) {
            setIsDisabledCalendar(true)
            confirmDates()
        }
        return () => {
            setIsDisabledCalendar(false)
        }
    }, [isConfirmed])

    useEffect(() => {
        if (isFetching || !data) return
        setIsDisabledCalendar(false)
    }, [isFetching])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={close}
            isMobileFullscreen={true}
            testIdPrefix={testIdPrefix ? `${testIdPrefix}-calendar` : undefined}
        >
            <>
                <ModalHeader isFullWidth>
                    <Text variant="body" mb={4} color="grayscale_900">
                        <FormattedMessage
                            id="global.calendar.selected_date_label"
                            defaultMessage="Data pobytu"
                        />
                    </Text>

                    {currentDate ? (
                        <Box direction="row" gap={4}>
                            <Text variant="bodyLarge" color="secondary_500">
                                {formatDates([currentDate])}
                            </Text>
                        </Box>
                    ) : (
                        <Text variant="bodyLarge" color="secondary_500">
                            <FormattedMessage
                                id="global.calendar.selected_date_placeholder"
                                defaultMessage="Wybierz dzień przyjazdu"
                            />
                        </Text>
                    )}
                </ModalHeader>
                {isFetching ? (
                    <CalendarLoader />
                ) : (
                    <>
                        <Box>
                            <Calendar
                                mode={'single'}
                                handleDatesChosen={handleSetDates}
                                datesInfo={data ? data : null}
                                isLimitedRange={true}
                                isDisabled={isDisabledCalendar}
                                isCurrentDayDisabled={isCurrentDayDisabled}
                                date={
                                    currentDate === ''
                                        ? null
                                        : moment(currentDate)
                                }
                                testIdPrefix={testIdPrefix ?? undefined}
                            />
                        </Box>
                        <ModalFooter gap={16} align="center" isFullWidth>
                            <CalendarModalLegend
                                isEventsLegendVisible={isEventsLegendVisible}
                                isHolidaysLegendVisible={
                                    isHolidaysLegendVisible
                                }
                                eventsData={data?.events}
                                testIdPrefix={testIdPrefix}
                                openHoursModal={openHoursModal}
                            />

                            {isMobile && (
                                <Button onClick={confirmDates}>
                                    <FormattedMessage
                                        id="global.btn.confirm"
                                        defaultMessage="Potwierdź"
                                    />
                                </Button>
                            )}
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default SelfcareCalendarModal
